<script setup lang="ts">
import Button from "@/components/form/Button.vue"
import Textarea from "@/components/form/Textarea.vue"
import { useModal } from "@/stores/modal"
import { TransitionChild, TransitionRoot } from "@headlessui/vue"
import { computed, watch } from "vue"

const modalStore = useModal()
const isOpen = computed(() => modalStore.isOpen)
const items = computed(() => modalStore.items)

watch(isOpen, () => {
  if (isOpen.value) {
    document.documentElement.style.overflowY = "hidden"
  } else {
    document.documentElement.style.overflowY = "auto"
  }
})
</script>

<template>
  <TransitionRoot
    :show="modalStore.isOpen"
    as="div"
    enter="duration-300 ease-out"
    enter-from="opacity-0"
    enter-to="opacity-100"
    leave="duration-200 ease-in"
    leave-from="opacity-100"
    leave-to="opacity-0"
  >
    <div v-for="(item, index) in items" :key="index" class="relative z-[51]">
      <TransitionChild
        enter="duration-300 ease-out"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="duration-200 ease-in"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <div
          class="fixed inset-0 bg-black bg-opacity-[20%] transition-opacity"
          @click="modalStore.closeModal()"
        ></div>
      </TransitionChild>
      <div class="fixed top-1/2 left-1/2 -translate-y-1/2 -translate-x-1/2">
        <TransitionChild
          enter="duration-300 ease-out"
          enter-from="opacity-0 scale-95"
          enter-to="opacity-100 scale-100"
          leave="duration-200 ease-in"
          leave-from="opacity-100 scale-100"
          leave-to="opacity-0 scale-95"
        >
          <div
            class="w-11/12 px-6 transition-all transform bg-white rounded-lg shadow-xl sm:w-auto dark:bg-gray-800 py-9"
            :class="item.classes"
          >
            <div>
              <div
                v-if="item.hasCloseIcon"
                class="absolute top-0 right-0 hidden pt-3 pr-1 sm:block"
              >
                <button
                  type="button"
                  class="text-gray-400 bg-white dark:bg-gray-800 focus:outline-none"
                  @click="modalStore.closeModal()"
                >
                  <span class="sr-only">{{ $t("general.close") }}</span>
                  <XIcon class="w-6 h-6" aria-hidden="true" />
                </button>
              </div>
              <div class="flex flex-col justify-center gap-6">
                <div v-if="item.icon?.name" class="flex justify-center">
                  <component
                    :is="item.icon?.name"
                    class="w-12 h-12 text-primary"
                  />
                </div>
                <div class="flex flex-col gap-2">
                  <h3
                    v-if="item.title || item.subtitle"
                    class="text-lg font-medium leading-6 dark:text-white light:text-gray-900"
                  >
                    {{ item.title }}
                    <b>{{ item.subtitle }}</b>
                  </h3>
                  <p
                    v-if="typeof item.text === 'string'"
                    id="text-content"
                    class="text-sm text-gray-500"
                  >
                    {{ item.text }}
                  </p>
                  <component :is="item.text" v-else-if="item.text" />
                  <Textarea
                    v-if="item.textArea?.isVisible"
                    :id="item.textArea.id"
                    v-model="item.textArea.modelValue"
                    :placeholder="item.textArea.placeholder"
                  />
                  <component
                    :is="item.component"
                    v-if="item.component"
                  ></component>
                </div>
                <div
                  v-if="item.hasButtons === true"
                  class="flex flex-wrap justify-center gap-6 sm:flex-nowrap"
                >
                  <Button
                    v-show="item.button?.cancel?.isVisible"
                    type="button"
                    :text="`${
                      item?.button?.cancel?.text || $t('general.cancel')
                    }`"
                    class-button="secondary w-full sm:max-w-[50%]"
                    :class="[
                      item.button?.cancel?.styles && item.button?.cancel?.styles
                    ]"
                    data-test-id="modal-cancel-button"
                    @click.capture="() => item.button?.cancel?.click()"
                  />
                  <Button
                    ref="cancelButtonRef"
                    type="button"
                    :text="`${
                      item.button?.confirmation.text || $t('general.ok')
                    }`"
                    class-button="w-full sm:max-w-[50%]"
                    :class="[
                      item.button?.confirmation.styles &&
                        item.button?.confirmation.styles
                    ]"
                    data-test-id="modal-confirm-button"
                    @click.capture="item.button?.confirmation.click()"
                  />
                </div>
              </div>
            </div>
          </div>
        </TransitionChild>
      </div>
    </div>
  </TransitionRoot>
</template>
